'use client';

import React, { useRef, useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { useLanguage } from '@/contexts/LanguageContext';

const ProfileSection = () => {
  const { t } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const features = [
    { icon: 'LinkIcon', key: 'feature1' },
    { icon: 'ShareIcon', key: 'feature2' },
    { icon: 'ChartBarIcon', key: 'feature3' },
  ];

  return (
    <section ref={sectionRef} id="perfil" className="py-32 bg-[#F4F7FC]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
          {/* Left: Text */}
          <div className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            <div className="inline-flex items-center gap-2 mb-6">
              <div className="w-1.5 h-1.5 rounded-full bg-[#1D6FE8]" />
              <span className="text-sm font-body font-medium text-[#1D6FE8] tracking-wide uppercase">
                {t('profile.eyebrow')}
              </span>
            </div>
            <h2 className="font-display font-bold text-[#0A1628] leading-tight tracking-tight mb-6"
              style={{ fontSize: 'clamp(2rem, 4vw, 3rem)' }}>
              {t('profile.headline')}{' '}
              <span className="gradient-text-blue">{t('profile.headlineAccent')}</span>
            </h2>
            <p className="font-body text-lg text-[#4A5568] leading-relaxed mb-10">
              {t('profile.subtitle')}
            </p>

            {/* Features */}
            <div className="space-y-5">
              {features.map((feature, i) => (
                <div key={feature.key} className={`flex items-start gap-4 transition-all duration-500 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'}`}
                  style={{ transitionDelay: `${200 + i * 100}ms` }}>
                  <div className="w-10 h-10 rounded-xl bg-white border border-[#E8EDF5] flex items-center justify-center flex-shrink-0 shadow-card">
                    <Icon name={feature.icon as any} size={18} className="text-[#1D6FE8]" />
                  </div>
                  <div>
                    <div className="font-display font-semibold text-[#0A1628] text-base mb-0.5">
                      {t(`profile.${feature.key}`)}
                    </div>
                    <div className="font-body text-sm text-[#4A5568]">
                      {t(`profile.${feature.key}Desc`)}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* URL example */}
            <div className="mt-10 flex items-center gap-3 bg-white rounded-2xl px-5 py-4 border border-[#E8EDF5] shadow-card">
              <Icon name="LinkIcon" size={18} className="text-[#C9A84C] flex-shrink-0" />
              <span className="font-mono text-sm text-[#4A5568] truncate">{t('profile.exampleUrl')}</span>
              <div className="ml-auto flex-shrink-0">
                <div className="w-6 h-6 rounded-lg bg-[#EEF4FD] flex items-center justify-center">
                  <Icon name="ClipboardDocumentIcon" size={13} className="text-[#1D6FE8]" />
                </div>
              </div>
            </div>
          </div>

          {/* Right: Profile Mockup */}
          <div className={`transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-[#1D6FE8]/8 to-[#C9A84C]/8 rounded-3xl blur-2xl scale-105" />

              <div className="relative bg-white rounded-3xl shadow-glass border border-[#E8EDF5] overflow-hidden">
                {/* Cover */}
                <div className="h-36 bg-gradient-to-br from-[#0A1628] via-[#1D3A6E] to-[#1D6FE8] relative overflow-hidden">
                  <div className="absolute inset-0">
                    <div className="absolute top-4 right-8 w-24 h-24 rounded-full bg-white/5 blur-xl" />
                    <div className="absolute bottom-2 left-12 w-16 h-16 rounded-full bg-[#C9A84C]/20 blur-lg" />
                  </div>
                  <div className="absolute top-4 right-4 flex items-center gap-1.5 bg-white/15 backdrop-blur-sm rounded-full px-3 py-1.5">
                    <Icon name="CheckBadgeIcon" size={14} className="text-[#C9A84C]" />
                    <span className="text-white text-xs font-body font-medium">Verified</span>
                  </div>
                </div>

                <div className="px-6 pb-6">
                  {/* Avatar row */}
                  <div className="flex items-end justify-between -mt-12 mb-5">
                    <div className="relative">
                      <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-[#1D6FE8] to-[#0A1628] border-4 border-white shadow-card flex items-center justify-center">
                        <Icon name="UserIcon" size={40} className="text-white" />
                      </div>
                      <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-[#16A34A] rounded-full border-2 border-white" />
                    </div>
                    <div className="flex gap-2 mt-2">
                      {['GlobeAltIcon', 'ChatBubbleLeftRightIcon', 'ShareIcon'].map((icon) => (
                        <div key={icon} className="w-9 h-9 rounded-xl bg-[#F4F7FC] flex items-center justify-center hover:bg-[#EEF4FD] transition-colors cursor-pointer">
                          <Icon name={icon as any} size={16} className="text-[#1D6FE8]" />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Info */}
                  <div className="mb-4">
                    <h3 className="font-display font-bold text-2xl text-[#0A1628]">{t('hero.profileName')}</h3>
                    <p className="text-sm font-body text-[#1D6FE8] font-medium mt-0.5">{t('hero.profileTitle')}</p>
                    <div className="flex items-center gap-1.5 mt-1.5">
                      <Icon name="MapPinIcon" size={13} className="text-[#4A5568]" />
                      <span className="text-xs font-body text-[#4A5568]">Global · Remote</span>
                    </div>
                  </div>

                  {/* Bio */}
                  <p className="text-sm font-body text-[#4A5568] leading-relaxed mb-5">
                    {t('hero.profileBio')}
                  </p>

                  {/* Services */}
                  <div className="mb-5">
                    <p className="text-xs font-body font-semibold text-[#0A1628] uppercase tracking-wider mb-3">{t('profile.services')}</p>
                    <div className="space-y-2">
                      {[t('profile.service1'), t('profile.service2'), t('profile.service3')].map((service) => (
                        <div key={service} className="flex items-center gap-2.5">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#1D6FE8]" />
                          <span className="text-sm font-body text-[#0A1628]">{service}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* URL */}
                  <div className="flex items-center gap-2 bg-[#F4F7FC] rounded-xl px-3 py-2.5 mb-5">
                    <Icon name="LinkIcon" size={14} className="text-[#C9A84C] flex-shrink-0" />
                    <span className="text-xs font-mono text-[#4A5568] truncate">{t('hero.profileUrl')}</span>
                  </div>

                  {/* Buttons */}
                  <div className="grid grid-cols-2 gap-3">
                    <button className="flex items-center justify-center gap-2 bg-[#F4F7FC] text-[#0A1628] text-sm font-display font-semibold py-3 rounded-xl hover:bg-[#EEF4FD] transition-colors">
                      <Icon name="EnvelopeIcon" size={16} />
                      {t('profile.contactBtn')}
                    </button>
                    <button className="flex items-center justify-center gap-2 bg-[#0A1628] text-white text-sm font-display font-semibold py-3 rounded-xl hover:bg-[#1D6FE8] transition-colors">
                      <Icon name="UserPlusIcon" size={16} />
                      {t('profile.registerBtn')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProfileSection;
